// ============================================================================
// WhatsNew - a lightweight in-app changelog.
//
// v1 is a static, bundled list (no schema, ships immediately). To announce a
// release, add an entry to the top of CHANGELOG with a new `id`; anyone who
// hasn't opened that version sees an unread dot on the sidebar trigger until
// they view it. Can graduate to a DB-backed table later without changing the
// trigger/modal.
// ============================================================================
import { useState } from "react";
import { Modal } from "./ui";
import { IconSparkles } from "./icons";
import { C } from "../lib/tokens";

// Newest first. `id` drives the unread indicator - bump it for each release.
export const CHANGELOG = [
  {
    id: "2026-07-assistant-voice-and-clinical",
    date: "July 2026",
    title: "Ask the assistant out loud, and ask it about clinical data",
    sections: [
      { heading: "Dictate instead of typing", items: [
        "The AI Practice Assistant has a Dictate button. Press it, ask your question out loud, press Stop, and the text appears in the box.",
        "Nothing is sent until you press Send, so check the text first. A misheard word is a quick edit rather than a wrong answer.",
        "Recording stops on its own after two minutes. If you dictate a second time without having typed anything, the new attempt replaces the first rather than stacking on top of it.",
        "If the microphone hears nothing, it says so rather than inventing words.",
        "Your browser will ask permission the first time. If you decline and change your mind, allow microphone access for the site in your browser settings.",
        "Audio is transcribed and discarded. It is never stored.",
      ] },
      { heading: "The assistant can now see clinical data", items: [
        "Until now the assistant could answer questions about patients, appointments, care gaps, referrals and recall. It could not see diagnoses, lab results, vitals, medications, care management contacts, encounters, tasks, consents or monthly billing.",
        "All of those are now available to it, so a question about an A1c, a diagnosis code, or last month's touchpoints has an answer.",
        "It understands how this system writes things. MRN-1031 and MRN 1031 find the same patient, A1c finds the HbA1c readings, and diabetic panel means the diabetes panel.",
        "Trends work too. It can tell you whose A1c went up since their last visit, not just what the current value is.",
        "When the assistant genuinely cannot answer something, it now says so in plain English instead of returning a database error.",
      ] },
      { heading: "Questions worth trying", items: [
        "How many patients have a diagnosis of E11.9?",
        "Who are the patients with type 2 diabetes?",
        "What is the average A1c in my diabetic panel?",
        "Show me patients whose A1c went up since their last visit.",
        "Who has a systolic blood pressure over 140?",
        "How many open care gaps are there across the practice?",
        "How many touchpoints did we log in the last 30 days?",
        "Which members meet their contact requirements this month?",
        "Which patients are on metformin?",
        "Which lab results have not been reviewed yet?",
        "Break down the recall list by source.",
        "How many tasks are overdue?",
      ] },
      { heading: "Fixes", items: [
        "Asking how many of something now returns a count. Some of these questions previously came back empty, which read as none when the real answer was a number.",
        "Asking who is overdue for a measurement no longer returns the entire patient list when the measurement name does not match what is recorded.",
        "Questions that span the whole practice are no longer squeezed into a single-panel or single-measure answer.",
      ] },
    ],
  },
  {
    id: "2026-07-care-management",
    date: "July 2026",
    title: "Care management: assessments, goals, and transitions",
    sections: [
      { heading: "The comprehensive assessment", items: [
        "Members the health plan flags as needing care management now have a structured assessment, not just a date. Find them under Care Management > Assessments.",
        "Which assessment you complete depends on the member's plan, and PracticeOS picks it for you - 8 domains for a Standard Plan member, 22 for a Tailored Plan member. The domains come straight from the AMH and Tailored Care Management provider manuals.",
        "The Why required column names the priority population the plan sent, so you can see at a glance why a member is on the list.",
        "Answers save as you go and the assessment reopens where you left it. A 22-domain assessment across two or three contacts is normal.",
        "Domains that do not apply - Early Intervention questions for an adult, permanency planning for someone not in foster care - can be marked Not applicable, which counts as answered.",
        "Some domains arrive prefilled from the care plan and say so. Worth checking with the member, since the care plan may be months old.",
        "Completing an assessment requires a clinician credential, minimum RN or LCSW, as the manuals require. The credential is recorded with the assessment.",
        "Once complete, the health plan is told on the next PRL export and the care plan is due within 30 days - the date shows on the member.",
        "Reassessment prompts appear when a member is admitted to hospital or has two ED visits since their last assessment, plus the annual reminder. The judgement calls - a significant change in circumstances - are still yours.",
        "Members already assessed before this feature existed are shown as such rather than as never assessed, and their annual clock runs from the real date.",
        "Available on the patient chart under Screenings for any member you manage, with no add-on required.",
      ] },
      { heading: "Care plan goals you can actually measure", items: [
        "Care plan goals are now tracked individually, with a target you can set against a lab or vital - an A1c under 7, a blood pressure under 140.",
        "PracticeOS tells you whether the latest reading meets the target, and says No recent value rather than guessing when the measurement is too old to judge.",
        "Log what a contact did for each goal - advanced, discussed, or a barrier found - and a goal that has resisted several contacts is flagged as stalled with the barrier that is blocking it.",
        "A new Care plan goals view in the Quality Dashboard shows attainment and stalled goals by domain or by care manager.",
        "Goals can close automatically when a measured value clearly meets the target, but only for measures a clinician at your practice has signed off, and only for goals you opt in individually.",
      ] },
      { heading: "Transitional care after a discharge", items: [
        "Discharge episodes now carry the full checklist both provider manuals require - not just the outreach call and medication reconciliation, but obtaining the discharge summary, the clinical handoff, and a face-to-face follow-up visit.",
        "An attempt counts. Reaching the hospital social worker when the member was unavailable is documented work, and there is an Attempted outcome for it.",
        "Tailored Plan discharges include the 90-day post-discharge transition plan. Marking it done creates the plan itself, effective that day and running for 90 days.",
        "Anything other than a clean completion asks for a short note, because the note is what evidences what was tried.",
        "A performance summary above the worklist shows outreach on time, late, and never as three separate numbers, so a team reaching everyone slowly reads differently from one missing people.",
      ] },
      { heading: "Screens that know about each other", items: [
        "Risk Review and Plan Disagreements now point at each other, and say when they are likely the same event - a tier change of ours is often what created the disagreement.",
        "A quality gap now shows the care plan goal already working it, with the target and the latest value. It flags when the evidence is too old to rely on, and when a goal can only partly prove a measure.",
        "Expanding a claim shows the billing period and the contacts behind it, which is what an appeal needs.",
        "Discharge episodes and registry members carry a strip showing open goals, quality gaps, and any risk event - so a post-discharge call can pick up the goal that stalled on a copay.",
      ] },
      { heading: "Finding things again", items: [
        "The discharge worklist now holds only live episodes. Completed and closed ones moved to History, which you can search by member name or MRN.",
        "The same for the handoff registry - open handoffs are separate from history, and both are searchable.",
        "Both load a recent window by default rather than everything ever, so they stay quick as a practice grows.",
      ] },
      { heading: "Fixes", items: [
        "Flow Mode can now be exited on a phone. The Exit button was being pushed off the edge of narrow screens.",
        "Empty screens now show their explanation. Twenty of them had helpful text written that never appeared.",
        "Wider modals on desktop where the content needed it, including logging a touchpoint and the CHW engagement forms.",
        "Filter tabs across the transitions screens now scroll on a phone rather than squeezing until the last option is unreadable.",
      ] },
    ],
  },
  {
    id: "2026-07-email",
    date: "July 2026",
    title: "Email reminders for preventive care",
    sections: [
      { heading: "Reminding patients by email", items: [
        "Patients with open preventive care gaps can now get an email nudge with a link to sign in and request an appointment - one message covering everything that's due, not one per item.",
        "Turn it on and set how often patients can be contacted under Settings > Practice Setup > Communications. Nothing goes out until you enable it.",
        "Reminder emails appear in the patient's Messages tab alongside texts and portal messages, so you can see what was sent without leaving the chart.",
      ] },
      { heading: "You decide what the email says", items: [
        "Choose whether reminders name the specific service ('Blood pressure check') or stay general ('a health reminder from your care team').",
        "Preview what patients see before you decide - both options side by side, with the real email and subject line.",
        "Sensitive categories are never named either way. Behavioral health, substance use, HIV, reproductive health, and STI reminders always appear as a count only, no matter which option you pick.",
      ] },
      { heading: "Keeping patients reachable", items: [
        "Bad email addresses now show a warning on the patient's chart instead of failing quietly, so you can correct them. Fixing the address clears the warning and resumes reminders.",
        "Patients who mark a reminder as spam, or whose address is undeliverable, are automatically excluded from future emails.",
        "Patients can turn off email reminders themselves in the portal under Account > Communication. You can also set it for them on the chart.",
      ] },
      { heading: "Seeing how it's going", items: [
        "A new Email tab in Reports shows how many reminders went out, how many landed, and why any were held back.",
      ] },
      { heading: "Security", items: [
        "Several fixes to what patients can change from the portal. The one you may notice: if staff revoke someone's proxy access to a patient's record, the patient can no longer reinstate it themselves - that now requires staff.",
      ] },
    ],
  },
  {
    id: "2026-07",
    date: "July 2026",
    title: "A big usability update",
    sections: [
      { heading: "Work faster", items: [
        "Bulk actions on Tasks - select several and complete or reassign them at once.",
        "Bulk invite on Recall - send booking invites to several patients at once, with a confirmation first.",
        "Save your worklist filters as reusable 'My views' on Tasks and the Care Management registry.",
        "Undo for completing a task or declining a waitlist candidate.",
      ] },
      { heading: "Stay on top of things", items: [
        "End-of-day digest on your dashboard each afternoon - visits done, notes to sign, billing on hold, and tomorrow's load - with a reminder when it's ready.",
        "Aging unsigned notes now surface on the attention bell, so nothing sits unbilled.",
        "Optional desktop alerts when a queue needs attention while PracticeOS is in the background.",
        "Notification-bell items now open the right screen already filtered to what you clicked.",
      ] },
      { heading: "At the point of care", items: [
        "Patient charts show quick-glance chips - allergies, meds, open care gaps, and next visit - on every tab.",
        "Clinical notes auto-save a local draft as you type, with one-click restore if a tab closes.",
      ] },
      { heading: "Works on your phone", items: [
        "A full mobile pass - the dashboard, inbox, tasks, and schedule are now usable on a phone, with the schedule showing a clean day agenda.",
      ] },
      { heading: "Polish throughout", items: [
        "Cleaner loading with skeleton placeholders instead of spinners.",
        "Friendlier empty states that offer the obvious next action.",
        "Consistent toast notifications app-wide, replacing abrupt pop-up dialogs.",
        "The sidebar now leads with your practice name, with tidier navigation.",
        "Clean printing for charts, reports, and notes.",
        "This 'What's new' panel, so you can keep up with changes.",
      ] },
    ],
  },
];

// Unread state for the latest release, shared by the sidebar trigger below and
// the account menu (Layout footer), which shows the dot on the avatar instead.
// Same storage key either way, so opening it from one place clears both.
export function useWhatsNew() {
  const latestId = CHANGELOG[0] && CHANGELOG[0].id;
  const [seen, setSeen] = useState(() => {
    try { return localStorage.getItem("pos_whatsnew_seen"); } catch (e) { return null; }
  });
  const unread = Boolean(latestId && seen !== latestId);
  const markSeen = () => {
    if (!latestId) return;
    try { localStorage.setItem("pos_whatsnew_seen", latestId); } catch (e) { /* ignore */ }
    setSeen(latestId);
  };
  return { unread, markSeen };
}

export function WhatsNewModal({ onClose }) {
  return (
    <Modal title="What's new" onClose={onClose} maxWidth={540}>
      {CHANGELOG.map((rel) => (
        <div key={rel.id} style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: C.textPrimary }}>{rel.title}</span>
            <span style={{ fontSize: 12, color: C.textTertiary }}>{rel.date}</span>
          </div>
          {(rel.sections || [{ items: rel.items || [] }]).map((sec, si) => (
            <div key={si} style={{ marginBottom: sec.heading ? 12 : 0 }}>
              {sec.heading && (
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: C.brandText, margin: "8px 0 6px" }}>{sec.heading}</div>
              )}
              <ul style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 7 }}>
                {(sec.items || []).map((it, i) => (
                  <li key={i} style={{ fontSize: 13, color: C.textSecondary, lineHeight: 1.45 }}>{it}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ))}
      <div style={{ fontSize: 11, color: C.textTertiary, borderTop: "0.5px solid " + C.borderLight, paddingTop: 10 }}>
        Thanks for using PracticeOS. More on the way.
      </div>
    </Modal>
  );
}

export default function WhatsNew({ collapsed }) {
  const [open, setOpen] = useState(false);
  const { unread, markSeen } = useWhatsNew();

  const openIt = () => {
    setOpen(true);
    markSeen();
  };

  return (
    <>
      <button
        onClick={openIt}
        className="pos-nav-item"
        title="What's new"
        style={{
          display: "flex", alignItems: "center", gap: 10,
          width: collapsed ? "auto" : "calc(100% - 24px)",
          margin: collapsed ? "0 auto" : "0 12px",
          padding: collapsed ? "8px" : "8px 10px",
          background: "transparent", color: C.onAccent, opacity: 0.9,
          border: "none", borderRadius: 8, cursor: "pointer",
          fontFamily: "inherit", fontSize: 13, fontWeight: 500,
          justifyContent: collapsed ? "center" : "flex-start", position: "relative",
        }}
      >
        <span style={{ display: "inline-flex", flexShrink: 0, position: "relative" }}>
          <IconSparkles size={17} />
          {unread && collapsed && (
            <span style={{ position: "absolute", top: -2, right: -2, width: 7, height: 7, borderRadius: 999, background: C.warnSolid }} />
          )}
        </span>
        {!collapsed && <span style={{ flex: 1, textAlign: "left" }}>What's new</span>}
        {!collapsed && unread && (
          <span style={{ width: 7, height: 7, borderRadius: 999, background: C.warnSolid, flexShrink: 0 }} />
        )}
      </button>

      {open && <WhatsNewModal onClose={() => setOpen(false)} />}
    </>
  );
}
